/**
 * RecordModal.jsx
 * ---------------
 * Modal for creating and editing records with dynamic form based on table schema
 */

import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import dataManagementService from '../../services/dataManagementService';

const RecordModal = ({ 
  mode, // 'create' or 'edit'
  record, // Existing record data (for edit mode)
  fields, // Field definitions from schema
  onSave, 
  onCancel 
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Initialize form data
    if (mode === 'edit' && record) {
      // Load existing record data
      const initialData = {};
      fields.forEach(field => {
        initialData[field.name] = record[field.name] || '';
      });
      setFormData(initialData);
    } else {
      // Initialize empty form
      const initialData = {};
      fields.forEach(field => {
        initialData[field.name] = field.type === 'boolean' ? false : '';
      });
      setFormData(initialData);
    }
  }, [mode, record, fields]);

  const handleChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    const validation = dataManagementService.validateRecord(formData, fields);
    
    if (!validation.valid) {
      setErrors(validation.errors);
      return;
    }
    
    setSaving(true);
    
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Failed to save record:', error);
      setErrors({ _general: error.message || 'Failed to save record' });
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.name];
    const hasError = errors[field.name];

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
      case 'file':
      case 'image':
        return (
          <input
            type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'}
            value={value || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={`record-input ${hasError ? 'input-error' : ''}`}
            required={field.required}
            placeholder={
              field.type === 'phone' ? '9876543210' :
              field.type === 'url' ? 'https://example.com' :
              field.type === 'file' ? 'https://example.com/file.pdf' :
              field.type === 'image' ? 'https://example.com/image.jpg' : ''
            }
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={`record-input record-textarea ${hasError ? 'input-error' : ''}`}
            required={field.required}
            rows={4}
          />
        );

      case 'number':
      case 'currency':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleChange(field.name, e.target.value ? parseFloat(e.target.value) : '')}
            className={`record-input ${hasError ? 'input-error' : ''}`}
            required={field.required}
            min={field.type === 'currency' ? 0 : undefined}
            step={field.type === 'currency' ? 0.01 : 'any'}
          />
        );

      case 'percentage':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleChange(field.name, e.target.value ? parseFloat(e.target.value) : '')}
            className={`record-input ${hasError ? 'input-error' : ''}`}
            required={field.required}
            min={0}
            max={100}
            step={0.1}
          />
        );

      case 'rating':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => handleChange(field.name, e.target.value ? parseFloat(e.target.value) : '')}
            className={`record-input ${hasError ? 'input-error' : ''}`}
            required={field.required}
            min={1}
            max={5}
            step={0.5}
          />
        );

      case 'date':
        // Convert ISO string to YYYY-MM-DD format for input
        const dateValue = value ? value.split('T')[0] : '';
        return (
          <input
            type="date"
            value={dateValue}
            onChange={(e) => handleChange(field.name, e.target.value ? new Date(e.target.value).toISOString() : '')}
            className={`record-input ${hasError ? 'input-error' : ''}`}
            required={field.required}
          />
        );

      case 'datetime':
        // Convert ISO string to datetime-local format
        const datetimeValue = value ? value.slice(0, 16) : '';
        return (
          <input
            type="datetime-local"
            value={datetimeValue}
            onChange={(e) => handleChange(field.name, e.target.value ? new Date(e.target.value).toISOString() : '')}
            className={`record-input ${hasError ? 'input-error' : ''}`}
            required={field.required}
          />
        );

      case 'color':
        return (
          <input
            type="color"
            value={value || '#000000'}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={`record-input record-color ${hasError ? 'input-error' : ''}`}
            required={field.required}
          />
        );

      case 'boolean':
        return (
          <label className="record-checkbox-label">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleChange(field.name, e.target.checked)}
              className="record-checkbox"
            />
            <span>{value ? 'Yes' : 'No'}</span>
          </label>
        );

      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={`record-input record-select ${hasError ? 'input-error' : ''}`}
            required={field.required}
          >
            <option value="">-- Select --</option>
            {(field.options || []).map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'multiselect':
        return (
          <select
            multiple
            value={Array.isArray(value) ? value : []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value);
              handleChange(field.name, selected);
            }}
            className={`record-input record-multiselect ${hasError ? 'input-error' : ''}`}
            required={field.required}
            size={Math.min((field.options || []).length, 5)}
          >
            {(field.options || []).map((option, idx) => (
              <option key={idx} value={option}>{option}</option>
            ))}
          </select>
        );

      case 'json':
        return (
          <textarea
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value || ''}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleChange(field.name, parsed);
              } catch {
                handleChange(field.name, e.target.value);
              }
            }}
            className={`record-input record-textarea record-json ${hasError ? 'input-error' : ''}`}
            required={field.required}
            rows={6}
            placeholder='{"key": "value"}'
          />
        );

      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={`record-input ${hasError ? 'input-error' : ''}`}
            required={field.required}
          />
        );
    }
  };

  return (
    <div className="record-modal-overlay">
      <div className="record-modal">
        <div className="record-modal-header">
          <h2>{mode === 'create' ? 'Add New Record' : 'Edit Record'}</h2>
          <button onClick={onCancel} className="btn-close" disabled={saving}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="record-modal-form">
          {errors._general && (
            <div className="record-error-general">
              <AlertCircle size={20} />
              <span>{errors._general}</span>
            </div>
          )}

          <div className="record-fields">
            {fields.map((field, index) => (
              <div key={index} className="record-field-group">
                <label className="record-field-label">
                  {field.name}
                  {field.required && <span className="required-indicator">*</span>}
                  <span className="field-type-badge">{field.type}</span>
                </label>
                {renderField(field)}
                {errors[field.name] && (
                  <span className="record-field-error">{errors[field.name]}</span>
                )}
              </div>
            ))}
          </div>

          <div className="record-modal-footer">
            <button 
              type="button" 
              onClick={onCancel} 
              className="btn-cancel"
              disabled={saving}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-save"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="spinner-small"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {mode === 'create' ? 'Create' : 'Save Changes'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RecordModal;


